import { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, FileText, Send, Satellite, Smartphone, Loader2 } from 'lucide-react';
import { useGnss, FIX_SHORT } from '../lib/gnss';
import { aiDescribe, aiSummary, photoNote } from '../lib/photoAi';
import { photoReportPdf, sharePdf, photosOf } from '../lib/photoReportPdf';
import { photoBook } from '../lib/photoBook';
import { BookOpen } from 'lucide-react';

export const DOC_SECTORS = ['water', 'gas', 'power', 'telecom', 'sewer', 'civil', 'building', 'garden', 'survey', 'inhouse', 'heat', 'leisure', 'other'];
export const ACTIVITIES = ['hike', 'trip', 'fishing', 'bike', 'ski', 'water', 'city', 'family', 'garden', 'event', 'other'];
const desktop = () => window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches;
const aiErr = (t, e) => toast.error(e?.message === 'license' ? t.fxNoLic : e?.message === 'byok' ? t.akBad : navigator.onLine ? t.fxAiFail : t.fxOffline);

// GNSS antenna connected? -> its fix, else phone GPS
export function GnssBadge({ t }) {
  const g = useGnss();
  const on = g.connected && g.fix && g.fix !== 'none';
  return (
    <span className={`pd-gnss ${on ? 'on' : ''}`} data-testid="photo-gnss-badge">
      {on ? <Satellite size={14} /> : <Smartphone size={14} />}
      {on ? `${t.fxAntenna}: ${FIX_SHORT[g.fix]}${g.hrms != null ? ` ±${Math.round(g.hrms * 100)} cm` : ''}` : t.fxPhoneGps}
    </span>
  );
}

// AI text for one photo (under the photo card)
export function PhotoAiLine({ t, p, sector, onUpdate, i }) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { const d = await aiDescribe(p, sector); onUpdate(p.id, { aiDesc: d.description, tags: d.tags, aiCat: d.category }); } catch (e) { aiErr(t, e); } setBusy(false); };
  return (
    <div className="pd-ai" data-testid={`photo-ai-${i}`}>
      {p.aiDesc && <p data-testid={`photo-ai-desc-${i}`}>{p.aiDesc}</p>}
      {(p.aiCat || p.tags?.length > 0) && <div className="pd-tags" data-testid={`photo-ai-tags-${i}`}>{p.aiCat && <b>{p.aiCat}</b>}{(p.tags || []).map((x) => <span key={x}>{x}</span>)}</div>}
      <button type="button" className="btn btn-outline btn-sm" disabled={busy} data-testid={`photo-ai-btn-${i}`} onClick={run}>{busy ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />} {p.aiDesc ? t.fxAiAgain : t.fxAiOne}</button>
    </div>
  );
}

// photo branch: sector, GNSS state, AI for all photos, report summary, 3-step PDF and sending
export default function PhotoDocBar({ t, lang, project, report, updateProject }) {
  const [busy, setBusy] = useState(null);
  const sector = project.docSector || '';
  const all = (project.reports || []).flatMap((r) => photosOf(r).map((p) => ({ r, p })));
  const patchPhoto = (rid, pid, patch) => updateProject((pr) => ({ ...pr, reports: pr.reports.map((r) => (r.id === rid ? { ...r, photos: r.photos.map((x) => (x.id === pid ? { ...x, ...patch } : x)) } : r)) }));
  const describeAll = async () => {
    const todo = all.filter(({ p }) => !p.aiDesc);
    if (!todo.length) return toast.message(t.fxAllDone);
    setBusy('ai');
    let n = 0;
    for (const { r, p } of todo) { try { const d = await aiDescribe(p, t[`fxS_${sector}`] || sector); patchPhoto(r.id, p.id, { aiDesc: d.description, tags: d.tags, aiCat: d.category }); n++; } catch (e) { aiErr(t, e); break; } }
    setBusy(null); if (n) toast.success(`${n} ${t.fxPhotos} ✓`);
  };
  const summarize = async () => {
    setBusy('sum');
    try {
      const items = all.map(({ r, p }) => [r.title, p.caption, photoNote(p), p.aiDesc].filter(Boolean).join(': '));
      const s = await aiSummary(items.length ? items : [report.text || ''], project.title);
      updateProject((pr) => ({ ...pr, aiSummary: s }));
      toast.success(t.fxSumDone);
    } catch (e) { aiErr(t, e); }
    setBusy(null);
  };
  const pdf = async (send) => {
    setBusy(send ? 'send' : 'pdf');
    try { const out = await photoReportPdf(project, t, lang); if (send) await sharePdf(out, project.title); else { const a = document.createElement('a'); a.href = URL.createObjectURL(out.blob); a.download = out.name; a.click(); } } catch { toast.error(t.fxPdfFail); }
    setBusy(null);
  };
  const book = async (style) => {
    setBusy(`book-${style}`);
    try { const out = await photoBook(project, t, lang, style); const a = document.createElement('a'); a.href = URL.createObjectURL(out.blob); a.download = out.name; a.click(); toast.success(t.pbDone); } catch (e) { if (e?.message === 'empty') toast.error(t.pbEmpty); else aiErr(t, e); }
    setBusy(null);
  };
  const B = (k, I, label, fn, cls = 'btn-outline') => <button type="button" className={`btn ${cls} btn-sm`} disabled={!!busy} data-testid={`photo-doc-${k}`} onClick={fn}>{busy === k ? <Loader2 size={14} className="spin" /> : <I size={14} />} {label}</button>;
  return (
    <div className="pd-bar" data-testid="photo-doc-bar">
      <div className="row">
        <label className="pit-num"><span>{t.fxSector}</span>
          <select data-testid="photo-doc-sector" value={sector} onChange={(e) => updateProject((pr) => ({ ...pr, docSector: e.target.value }))}>
            <option value="">–</option>{DOC_SECTORS.map((k) => <option key={k} value={k}>{t[`fxS_${k}`]}</option>)}
          </select>
        </label>
        {sector === 'leisure' && <label className="pit-num"><span>{t.fxActivity}</span>
          <select data-testid="photo-doc-activity" value={project.activity || ''} onChange={(e) => updateProject((pr) => ({ ...pr, activity: e.target.value }))}>
            <option value="">–</option>{ACTIVITIES.map((k) => <option key={k} value={k}>{t[`fxA_${k}`]}</option>)}
          </select></label>}
        <GnssBadge t={t} />
      </div>
      <div className="row">
        {B('ai', Sparkles, t.fxAiAll, describeAll)}
        {B('sum', Sparkles, t.fxSumBtn, summarize)}
        {B('pdf', FileText, t.fxPdf, () => pdf(false))}
        {B('send', Send, t.fxSend, () => pdf(true), 'btn-green')}
      </div>
      {desktop() && <div className="row pd-book" data-testid="photo-book-row"><b><BookOpen size={15} /> {t.pbTitle}</b>
        {B('book-report', BookOpen, t.pbReport, () => book('report'))}
        {B('book-story', BookOpen, t.pbStory, () => book('story'))}
        {B('book-print', BookOpen, t.pbPrint, () => book('print'))}
      </div>}
      {project.aiSummary && <p className="pd-sum" data-testid="photo-doc-summary"><b>{t.fxSummary}:</b> {project.aiSummary}</p>}
    </div>
  );
}
